import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TreeContext, TreeState } from '../store/treeStore/treeStore';
import { getStateFromDb } from '../store/treeStore/indexedDbHelper';
import { resetQuestionnaireAction } from '../store/treeStore/treeActions';
import { mapToTreeState } from '../helpers/FhirToTreeStateMapper';
import Modal from '../components/Modal/Modal';
import SpinnerBox from '../components/Spinner/SpinnerBox';
import { useTranslation } from 'react-i18next';
import Btn from '../components/Btn/Btn';
import './FrontPage.css';
import { useAuth } from '../contexts/AuthContext';
import {
    assignQuestionnaireToPatient,
    listPractitionerQuestionnaires,
    getPractitionerQuestionnaireById,
    listPractitionerTasks,
    deleteTask,
    ParticipationPayload,
} from '../services/practitionerService';
import { getQuestionnaireResponseById } from '../services/questionnaireResponseService';
import {
    createTherapyMaterial,
    deleteQuestionnaire,
    isPrivateResource,
    updateTherapyMaterial,
} from '../services/questionnaireService';
import { getQuestionnaireSaveErrorMessage } from '../helpers/questionnaireSaveError';
import TaskResponseModal from '../components/TaskResponseModal/TaskResponseModal';
import QuestionnairePreviewModal from '../components/QuestionnairePreviewModal/QuestionnairePreviewModal';
import TherapyMaterialPreviewModal from '../components/TherapyMaterialPreviewModal/TherapyMaterialPreviewModal';
import { QuestionnaireResponse, Questionnaire } from '../types/fhir';
import {
    getTargetUsers,
    getTargetUsersWithPermission,
    resolveUserId,
    XAuthApiException,
    ResolvedUser,
} from '../services/xAuthService';
import {
    RewardSettings,
    getProfile,
    initProfile,
    nextWeek,
    previousWeek,
    ProfileApiException,
    resetNachsorge,
    resetRewards,
    TherapyProfile,
    updateRewards,
} from '../services/profileService';
import FrontPageHeader, { MainTab } from '../components/FrontPage/FrontPageHeader';
import RestoreQuestionnaireModal from '../components/FrontPage/RestoreQuestionnaireModal';
import ViewToggle from '../components/FrontPage/ViewToggle';
import OwnershipFilter from '../components/FrontPage/OwnershipFilter';
import QuestionnaireList from '../components/FrontPage/QuestionnaireList';
import TaskList from '../components/FrontPage/TaskList';
import AssignQuestionnaireModal from '../components/FrontPage/AssignQuestionnaireModal';
import DeleteTaskModal from '../components/FrontPage/DeleteTaskModal';
import DeleteQuestionnaireModal from '../components/FrontPage/DeleteQuestionnaireModal';
import ProfileEnergySettings from '../components/FrontPage/ProfileEnergySettings';
import ProfileAgeRow from '../components/FrontPage/ProfileAgeRow';
import ProfileWeekActions from '../components/FrontPage/ProfileWeekActions';
import StartNachsorgeModal from '../components/FrontPage/StartNachsorgeModal';
import {
    QuestionnaireSummary,
    TaskSummary,
    TaskResource,
    PractitionerQuestionnaireBundle,
    TaskBundle,
    PatientInfo,
} from '../components/FrontPage/types';
import { createAssignmentWindows, toQuestionnaireSummaries, toTaskSummaries } from '../components/FrontPage/utils';
import {
    AftercareTaskMetadataCache,
    enrichAftercareTasks,
} from '../components/FrontPage/aftercareTaskMetadata';
import { getLatestTaskResponseId, shouldLoadTaskResponse } from '../components/FrontPage/taskResponse';
import { MOCK_TASK_BUNDLE, MOCK_QUESTIONNAIRE_RESPONSE, USE_MOCK_DATA } from '../mocks/mockData';
import { QuestionnaireKind, isNachsorgeKind, isTherapyMaterialKind } from '../types/QuestionnaireKind';
import { NachsorgeStartException, startNachsorgeWorkflow } from '../services/nachsorgeService';
import { prepareTherapyMaterialUpload } from '../components/TherapyMaterial/therapyMaterialUpload';
import { TaskListSelection } from '../components/FrontPage/TaskList';
import { createCombinedResponsesPdf } from '../helpers/pdf/combinedResponsesPdf';
import { downloadBlob } from '../helpers/downloadBlob';

const PAGE_SIZE = 20;

const sortByUpdatedAtDesc = <T extends { updatedAt?: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => {
        if (!a.updatedAt && !b.updatedAt) return 0;
        if (!a.updatedAt) return 1;
        if (!b.updatedAt) return -1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

const mergeUniqueById = <T extends { id: string }>(current: T[], incoming: T[]): T[] => {
    const byId = new Map<string, T>();
    current.forEach((item) => byId.set(item.id, item));
    incoming.forEach((item) => byId.set(item.id, item));
    return Array.from(byId.values());
};

const FrontPage = (): JSX.Element => {
    const { t, i18n } = useTranslation();
    const { dispatch } = useContext(TreeContext);
    const { logout, user } = useAuth();
    const history = useHistory();
    const location = useLocation<{ feedback?: string } | undefined>();
    const [stateFromStorage, setStateFromStorage] = useState<TreeState>();
    const [isLoading, setIsLoading] = useState(false);
    const uploadRef = useRef<HTMLInputElement>(null);
    const therapyMaterialUploadRef = useRef<HTMLInputElement>(null);
    const paginationSentinelRef = useRef<HTMLDivElement>(null);
    const [questionnaires, setQuestionnaires] = useState<QuestionnaireSummary[]>([]);
    const [questionnaireListError, setQuestionnaireListError] = useState<string | null>(null);
    const [isQuestionnaireListLoading, setIsQuestionnaireListLoading] = useState(false);
    const [isQuestionnaireListLoadingMore, setIsQuestionnaireListLoadingMore] = useState(false);
    const [questionnaireOffset, setQuestionnaireOffset] = useState(0);
    const [hasMoreQuestionnaires, setHasMoreQuestionnaires] = useState(true);
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<QuestionnaireSummary | null>(null);
    const [, setPatientUid] = useState('');
    const [assignError, setAssignError] = useState<string | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [assignmentFeedback, setAssignmentFeedback] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [patients, setPatients] = useState<PatientInfo[]>([]);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const [patientsError, setPatientsError] = useState<string | null>(null);
    const [mainTab, setMainTab] = useState<MainTab>('questionnaires');
    const [viewMode, setViewMode] = useState<'questionnaires' | 'tasks'>('questionnaires');
    const [profileUsers, setProfileUsers] = useState<PatientInfo[]>([]);
    const [isLoadingProfileUsers, setIsLoadingProfileUsers] = useState(false);
    const [profileUsersError, setProfileUsersError] = useState<string | null>(null);
    const [profileSearchTerm, setProfileSearchTerm] = useState('');
    const [selectedProfileUser, setSelectedProfileUser] = useState<PatientInfo | null>(null);
    const [selectedUserProfile, setSelectedUserProfile] = useState<TherapyProfile | null>(null);
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [profileActionFeedback, setProfileActionFeedback] = useState<{
        type: 'success' | 'error';
        message: string;
    } | null>(null);
    const [isStartNachsorgeModalOpen, setIsStartNachsorgeModalOpen] = useState(false);
    const [nachsorgeStartError, setNachsorgeStartError] = useState<string | null>(null);
    const [isUpdatingWeek, setIsUpdatingWeek] = useState(false);
    const [isUpdatingRewards, setIsUpdatingRewards] = useState(false);
    const [tasks, setTasks] = useState<TaskSummary[]>([]);
    const [taskResources, setTaskResources] = useState<Map<string, TaskResource>>(new Map());
    const [taskListError, setTaskListError] = useState<string | null>(null);
    const [isTaskListLoading, setIsTaskListLoading] = useState(false);
    const [isTaskListLoadingMore, setIsTaskListLoadingMore] = useState(false);
    const [taskOffset, setTaskOffset] = useState(0);
    const [hasMoreTasks, setHasMoreTasks] = useState(true);
    const [taskSearchTerm, setTaskSearchTerm] = useState('');
    const [taskToDelete, setTaskToDelete] = useState<TaskSummary | null>(null);
    const [isDeletingTask, setIsDeletingTask] = useState(false);
    const [deleteTaskError, setDeleteTaskError] = useState<string | null>(null);
    const [selectedTask, setSelectedTask] = useState<TaskSummary | null>(null);
    const [pdfSelectedTaskIds, setPdfSelectedTaskIds] = useState<Set<string>>(new Set());
    const [pdfExportProgress, setPdfExportProgress] = useState<TaskListSelection['progress']>(null);
    const [pdfExportFeedback, setPdfExportFeedback] = useState<TaskListSelection['feedback']>(null);
    const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse | null>(null);
    const [isLoadingResponse, setIsLoadingResponse] = useState(false);
    const [responseError, setResponseError] = useState<string | null>(null);
    const [previewQuestionnaire, setPreviewQuestionnaire] = useState<Questionnaire | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [questionnaireToPreview, setQuestionnaireToPreview] = useState<QuestionnaireSummary | null>(null);
    const [questionnaireToDelete, setQuestionnaireToDelete] = useState<QuestionnaireSummary | null>(null);
    const [isDeletingQuestionnaire, setIsDeletingQuestionnaire] = useState(false);
    const [deleteQuestionnaireError, setDeleteQuestionnaireError] = useState<string | null>(null);
    const [therapyMaterialToUpload, setTherapyMaterialToUpload] = useState<Questionnaire | null>(null);
    const [isUploadingTherapyMaterial, setIsUploadingTherapyMaterial] = useState(false);
    const [therapyMaterialUploadError, setTherapyMaterialUploadError] = useState<string | null>(null);
    const [isTherapyMaterialPrivate, setIsTherapyMaterialPrivate] = useState(false);
    // Therapy material has no editor; editing replaces it with an uploaded file.
    const [therapyMaterialReplaceTarget, setTherapyMaterialReplaceTarget] = useState<QuestionnaireSummary | null>(null);
    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [isPreparingEdit, setIsPreparingEdit] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const accessToken = user?.access_token as string | undefined;
    const therapistId = user?.sub as string | undefined;
    const activeResourceKind: QuestionnaireKind =
        mainTab === 'therapy-material' ? 'therapy-material' : mainTab === 'nachsorge' ? 'nachsorge' : 'questionnaire';
    const isTherapyMaterial = isTherapyMaterialKind(activeResourceKind);
    const isNachsorge = isNachsorgeKind(activeResourceKind);
    const managementViewMode: 'questionnaires' | 'tasks' = isNachsorge ? 'tasks' : viewMode;
    const activeResourceKindRef = useRef<QuestionnaireKind>(activeResourceKind);
    const questionnaireRequestIdRef = useRef(0);
    const taskRequestIdRef = useRef(0);
    const aftercareTaskMetadataCacheRef = useRef<AftercareTaskMetadataCache>(new Map());

    useEffect(() => {
        activeResourceKindRef.current = activeResourceKind;
    }, [activeResourceKind]);

    const resetManagementView = (): void => {
        questionnaireRequestIdRef.current += 1;
        taskRequestIdRef.current += 1;
        aftercareTaskMetadataCacheRef.current.clear();
        setViewMode('questionnaires');
        setQuestionnaires([]);
        setQuestionnaireListError(null);
        setIsQuestionnaireListLoading(false);
        setIsQuestionnaireListLoadingMore(false);
        setQuestionnaireOffset(0);
        setHasMoreQuestionnaires(true);
        setSearchTerm('');
        setTasks([]);
        setTaskResources(new Map());
        setTaskListError(null);
        setIsTaskListLoading(false);
        setIsTaskListLoadingMore(false);
        setTaskOffset(0);
        setHasMoreTasks(true);
        setTaskSearchTerm('');
        setPdfSelectedTaskIds(new Set());
        setPdfExportFeedback(null);
        setAssignmentFeedback(null);
        setSelectedQuestionnaire(null);
        setTaskToDelete(null);
        setSelectedTask(null);
        setQuestionnaireResponse(null);
        setResponseError(null);
        setQuestionnaireToPreview(null);
        setQuestionnaireToDelete(null);
        setTherapyMaterialToUpload(null);
        setTherapyMaterialUploadError(null);
        setIsUploadingTherapyMaterial(false);
        setIsTherapyMaterialPrivate(false);
        setTherapyMaterialReplaceTarget(null);
        setEditError(null);
    };

    const handleMainTabChange = (tab: MainTab): void => {
        if (tab !== mainTab) {
            resetManagementView();
        }
        if (tab === 'nachsorge') {
            setViewMode('tasks');
        }
        setMainTab(tab);
    };

    useEffect(() => {
        getStoredQuestionnaire();
    }, []);

    const resolvePatientNamesForTasks = useCallback(
        async (taskSummaries: TaskSummary[]): Promise<TaskSummary[]> => {
            if (USE_MOCK_DATA || !accessToken || taskSummaries.length === 0) {
                return taskSummaries;
            }

            const patientIdsToResolve = new Set<string>();
            taskSummaries.forEach((task) => {
                if (task.patientId && (!task.patientName || task.patientName === task.patientId)) {
                    patientIdsToResolve.add(task.patientId);
                }
            });

            if (patientIdsToResolve.size === 0) {
                return taskSummaries;
            }

            const patientNameMap = new Map<string, string>();
            await Promise.all(
                Array.from(patientIdsToResolve).map(async (patientId) => {
                    try {
                        const resolved = await resolveUserId(patientId, { accessToken });
                        const fullName =
                            resolved.firstName && resolved.lastName
                                ? `${resolved.firstName} ${resolved.lastName}`
                                : resolved.firstName || resolved.lastName || resolved.username || patientId;
                        patientNameMap.set(patientId, fullName);
                    } catch (resolveError) {
                        console.warn(`[FrontPage] Failed to resolve patient ${patientId} for task:`, resolveError);
                        patientNameMap.set(patientId, patientId);
                    }
                }),
            );

            return taskSummaries.map((task) => {
                if (task.patientId && patientNameMap.has(task.patientId)) {
                    return {
                        ...task,
                        patientName: patientNameMap.get(task.patientId) || task.patientName || task.patientId,
                    };
                }
                return task;
            });
        },
        [accessToken],
    );

    const loadQuestionnairesPage = useCallback(
        async (offset: number, append: boolean): Promise<void> => {
            const requestKind = activeResourceKind;
            const requestId = ++questionnaireRequestIdRef.current;
            if (!accessToken) {
                setQuestionnaires([]);
                setQuestionnaireListError(null);
                setIsQuestionnaireListLoading(false);
                setIsQuestionnaireListLoadingMore(false);
                setQuestionnaireOffset(0);
                setHasMoreQuestionnaires(false);
                return;
            }

            if (append) {
                setIsQuestionnaireListLoadingMore(true);
            } else {
                setAssignmentFeedback(null);
                setQuestionnaireListError(null);
                setIsQuestionnaireListLoading(true);
                setQuestionnaireOffset(0);
                setHasMoreQuestionnaires(true);
            }

            try {
                const response = await listPractitionerQuestionnaires<PractitionerQuestionnaireBundle>(
                    { offset, type: requestKind, mine: showOnlyMine },
                    { accessToken },
                );
                if (requestId !== questionnaireRequestIdRef.current || activeResourceKindRef.current !== requestKind) {
                    return;
                }

                const mapped = toQuestionnaireSummaries(
                    response,
                    requestKind === 'therapy-material' ? t('Untitled therapy material') : t('Untitled questionnaire'),
                    therapistId,
                );
                const pageCount = response.entry?.length ?? 0;

                setQuestionnaires((current) => {
                    const merged = append ? mergeUniqueById(current, mapped) : mapped;
                    return sortByUpdatedAtDesc(merged);
                });
                setQuestionnaireOffset(offset + pageCount);
                setHasMoreQuestionnaires(pageCount === PAGE_SIZE);
            } catch (error) {
                if (requestId !== questionnaireRequestIdRef.current || activeResourceKindRef.current !== requestKind) {
                    return;
                }
                const message = error instanceof Error ? error.message : t('Unknown error');
                setQuestionnaireListError(message);
            } finally {
                if (requestId !== questionnaireRequestIdRef.current || activeResourceKindRef.current !== requestKind) {
                    return;
                }
                if (append) {
                    setIsQuestionnaireListLoadingMore(false);
                } else {
                    setIsQuestionnaireListLoading(false);
                }
            }
        },
        [accessToken, activeResourceKind, showOnlyMine, t, therapistId],
    );

    const fetchQuestionnaires = useCallback(async (): Promise<void> => {
        await loadQuestionnairesPage(0, false);
    }, [loadQuestionnairesPage]);

    const fetchMoreQuestionnaires = useCallback(async (): Promise<void> => {
        if (!accessToken || isQuestionnaireListLoading || isQuestionnaireListLoadingMore || !hasMoreQuestionnaires) {
            return;
        }

        await loadQuestionnairesPage(questionnaireOffset, true);
    }, [
        accessToken,
        hasMoreQuestionnaires,
        isQuestionnaireListLoading,
        isQuestionnaireListLoadingMore,
        loadQuestionnairesPage,
        questionnaireOffset,
    ]);

    useEffect(() => {
        if (mainTab === 'questionnaires' || mainTab === 'therapy-material') {
            fetchQuestionnaires();
        }
    }, [mainTab, fetchQuestionnaires]);

    // The editor reports the outcome of saving an edit when it navigates back here. Runs after
    // the initial list load, which clears earlier feedback.
    useEffect(() => {
        const feedback = location.state?.feedback;
        if (feedback) {
            setAssignmentFeedback(feedback);
            history.replace({ ...location, state: undefined });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadTasksPage = useCallback(
        async (offset: number, append: boolean): Promise<void> => {
            const requestKind = activeResourceKind;
            const requestId = ++taskRequestIdRef.current;
            if (!accessToken) {
                setTasks([]);
                setTaskResources(new Map());
                setTaskListError(null);
                setIsTaskListLoading(false);
                setIsTaskListLoadingMore(false);
                setTaskOffset(0);
                setHasMoreTasks(false);
                return;
            }

            if (append) {
                setIsTaskListLoadingMore(true);
            } else {
                setTaskListError(null);
                setIsTaskListLoading(true);
                setTaskOffset(0);
                setHasMoreTasks(true);
            }

            try {
                let response: TaskBundle;
                if (USE_MOCK_DATA) {
                    if (append) {
                        setHasMoreTasks(false);
                        return;
                    }
                    console.log('Using Mock Data is enabled');
                    response = MOCK_TASK_BUNDLE as TaskBundle;
                } else {
                    response = await listPractitionerTasks<TaskBundle>({ offset, type: requestKind }, { accessToken });
                }

                if (requestId !== taskRequestIdRef.current || activeResourceKindRef.current !== requestKind) {
                    return;
                }

                const resourcesMap = new Map<string, TaskResource>();
                const mapped = toTaskSummaries(response, resourcesMap);
                const tasksWithAftercareMetadata =
                    requestKind === 'nachsorge'
                        ? await enrichAftercareTasks(
                              mapped,
                              (questionnaireId) =>
                                  getPractitionerQuestionnaireById<Questionnaire>(questionnaireId, { accessToken }),
                              aftercareTaskMetadataCacheRef.current,
                              (questionnaireId, error) => {
                                  console.warn(
                                      `[FrontPage] Failed to load aftercare Questionnaire ${questionnaireId}:`,
                                      error,
                                  );
                              },
                          )
                        : mapped;
                const resolvedTasks = await resolvePatientNamesForTasks(tasksWithAftercareMetadata);
                if (requestId !== taskRequestIdRef.current || activeResourceKindRef.current !== requestKind) {
                    return;
                }
                const pageCount = response.entry?.length ?? 0;

                setTasks((current) => {
                    const merged = append ? mergeUniqueById(current, resolvedTasks) : resolvedTasks;
                    return sortByUpdatedAtDesc(merged);
                });
                setTaskResources((current) => {
                    const merged = append ? new Map(current) : new Map<string, TaskResource>();
                    resourcesMap.forEach((value, key) => {
                        merged.set(key, value);
                    });
                    return merged;
                });
                setTaskOffset(offset + pageCount);
                setHasMoreTasks(!USE_MOCK_DATA && pageCount === PAGE_SIZE);
            } catch (error) {
                if (requestId !== taskRequestIdRef.current || activeResourceKindRef.current !== requestKind) {
                    return;
                }
                const message = error instanceof Error ? error.message : t('Unknown error');
                setTaskListError(message);
            } finally {
                if (requestId !== taskRequestIdRef.current || activeResourceKindRef.current !== requestKind) {
                    return;
                }
                if (append) {
                    setIsTaskListLoadingMore(false);
                } else {
                    setIsTaskListLoading(false);
                }
            }
        },
        [accessToken, activeResourceKind, resolvePatientNamesForTasks, t],
    );

    const fetchTasks = useCallback(async (): Promise<void> => {
        await loadTasksPage(0, false);
    }, [loadTasksPage]);

    const fetchMoreTasks = useCallback(async (): Promise<void> => {
        if (!accessToken || isTaskListLoading || isTaskListLoadingMore || !hasMoreTasks) {
            return;
        }

        await loadTasksPage(taskOffset, true);
    }, [accessToken, hasMoreTasks, isTaskListLoading, isTaskListLoadingMore, loadTasksPage, taskOffset]);

    useEffect(() => {
        if (mainTab !== 'profile' && managementViewMode === 'tasks') {
            fetchTasks();
        }
    }, [mainTab, managementViewMode, fetchTasks]);

    useEffect(() => {
        if (mainTab === 'profile') {
            return;
        }
        const shouldObserve =
            (managementViewMode === 'questionnaires' && hasMoreQuestionnaires) ||
            (managementViewMode === 'tasks' && hasMoreTasks);
        if (!shouldObserve) {
            return;
        }

        const sentinel = paginationSentinelRef.current;
        if (!sentinel) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (!first?.isIntersecting) {
                    return;
                }

                if (managementViewMode === 'questionnaires') {
                    void fetchMoreQuestionnaires();
                } else if (managementViewMode === 'tasks') {
                    void fetchMoreTasks();
                }
            },
            { rootMargin: '200px 0px' },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [mainTab, managementViewMode, hasMoreQuestionnaires, hasMoreTasks, fetchMoreQuestionnaires, fetchMoreTasks]);

    const getStoredQuestionnaire = async () => {
        const indexedDbState = await getStateFromDb();
        setStateFromStorage(indexedDbState);
    };

    const onReaderLoad = (event: ProgressEvent<FileReader>) => {
        if (event.target?.result) {
            const questionnaireObj = JSON.parse(event.target.result as string);
            const importedState = mapToTreeState(questionnaireObj);
            dispatch(resetQuestionnaireAction(importedState));
            setIsLoading(false);
            // Reset file input
            if (uploadRef.current) {
                uploadRef.current.value = '';
            }
            // Navigate to editor
            history.push('/editor');
        }
    };

    const uploadQuestionnaire = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsLoading(true);
        const reader = new FileReader();
        reader.onload = onReaderLoad;
        if (event.target.files && event.target.files[0]) reader.readAsText(event.target.files[0]);
    };

    const selectTherapyMaterialFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setAssignmentFeedback(null);
        setTherapyMaterialUploadError(null);
        const reader = new FileReader();

        reader.onload = (loadEvent): void => {
            try {
                if (typeof loadEvent.target?.result !== 'string') {
                    throw new Error('Could not read uploaded file');
                }

                setTherapyMaterialToUpload(prepareTherapyMaterialUpload(loadEvent.target.result));
            } catch (error) {
                console.error('[FrontPage] Invalid therapy material upload:', error);
                setTherapyMaterialToUpload(null);
                setTherapyMaterialUploadError(t('The selected file is not valid therapy material JSON.'));
            } finally {
                if (therapyMaterialUploadRef.current) {
                    therapyMaterialUploadRef.current.value = '';
                }
            }
        };
        reader.onerror = (): void => {
            setTherapyMaterialUploadError(t('Could not read uploaded file'));
            if (therapyMaterialUploadRef.current) {
                therapyMaterialUploadRef.current.value = '';
            }
        };
        reader.readAsText(file);
    };

    const closeTherapyMaterialUploadPreview = (): void => {
        setTherapyMaterialToUpload(null);
        setTherapyMaterialUploadError(null);
        setIsTherapyMaterialPrivate(false);
        setTherapyMaterialReplaceTarget(null);
    };

    const confirmTherapyMaterialUpload = async (): Promise<void> => {
        if (!therapyMaterialToUpload) {
            return;
        }
        if (!accessToken) {
            setTherapyMaterialUploadError(t('You must be logged in to upload therapy material.'));
            return;
        }

        setIsUploadingTherapyMaterial(true);
        setTherapyMaterialUploadError(null);

        const payload = therapyMaterialToUpload as unknown as Record<string, unknown>;
        const replaceTarget = therapyMaterialReplaceTarget;
        try {
            let feedback: string;
            if (replaceTarget?.versionId) {
                const { createdNewVersion } = await updateTherapyMaterial(
                    replaceTarget.id,
                    payload,
                    replaceTarget.versionId,
                    { accessToken },
                );
                feedback = createdNewVersion
                    ? t(
                          'Therapy material saved as a new version because patients had already answered it. Existing assignments keep the previous version.',
                      )
                    : t('Therapy material changes saved.');
            } else {
                await createTherapyMaterial(payload, { accessToken }, { isPrivate: isTherapyMaterialPrivate });
                feedback = t('Therapy material uploaded successfully.');
            }
            closeTherapyMaterialUploadPreview();
            await fetchQuestionnaires();
            setAssignmentFeedback(feedback);
        } catch (error) {
            setTherapyMaterialUploadError(
                replaceTarget
                    ? getQuestionnaireSaveErrorMessage(error, t, t('Failed to save changes'))
                    : error instanceof Error
                    ? error.message
                    : t('Failed to upload therapy material'),
            );
        } finally {
            setIsUploadingTherapyMaterial(false);
        }
    };
    const suggestRestore: boolean = stateFromStorage?.qItems ? Object.keys(stateFromStorage.qItems).length > 0 : false;

    const onDenyRestoreModal = (): void => {
        dispatch(resetQuestionnaireAction());
        setStateFromStorage(undefined);
    };

    const onConfirmRestoreModal = (): void => {
        dispatch(resetQuestionnaireAction(stateFromStorage));
        setStateFromStorage(undefined);
        // Navigate to editor
        history.push('/editor');
    };

    const closeAssignModal = (): void => {
        setSelectedQuestionnaire(null);
        setPatientUid('');
        setAssignError(null);
        setIsAssigning(false);
        setDownloadError(null);
        setIsDownloading(false);
        setPatients([]);
        setIsLoadingPatients(false);
        setPatientsError(null);
        setEditError(null);
    };

    const startEditQuestionnaire = async (): Promise<void> => {
        if (!selectedQuestionnaire) return;
        setEditError(null);

        if (isTherapyMaterial) {
            // The file dialog only opens directly from the click, so no request may come first.
            // The version the list was loaded at guards against overwriting a newer change.
            if (!selectedQuestionnaire.versionId) {
                setEditError(t('The version of this item is unknown. Reload the list and try again.'));
                return;
            }
            setTherapyMaterialReplaceTarget(selectedQuestionnaire);
            setTherapyMaterialUploadError(null);
            closeAssignModal();
            therapyMaterialUploadRef.current?.click();
            return;
        }

        if (!accessToken) {
            setEditError(t('You must be logged in to edit questionnaires.'));
            return;
        }

        setIsPreparingEdit(true);
        try {
            const questionnaire = await getPractitionerQuestionnaireById<Questionnaire>(selectedQuestionnaire.id, {
                accessToken,
            });
            const versionId = questionnaire.meta?.versionId;
            if (!questionnaire.id || !versionId) {
                throw new Error(t('The version of this item is unknown. Reload the list and try again.'));
            }

            dispatch(
                resetQuestionnaireAction({
                    ...mapToTreeState(questionnaire),
                    qEditTarget: { id: questionnaire.id, versionId, isPrivate: isPrivateResource(questionnaire) },
                }),
            );
            history.push('/editor');
        } catch (error) {
            setEditError(error instanceof Error ? error.message : t('Unknown error'));
        } finally {
            setIsPreparingEdit(false);
        }
    };

    const startAssign = async (questionnaire: QuestionnaireSummary): Promise<void> => {
        setAssignmentFeedback(null);
        setSelectedQuestionnaire(questionnaire);
        setPatientUid('');
        setAssignError(null);
        setPatientsError(null);
    };

    const getProfileErrorMessage = useCallback(
        (error: unknown, fallback: string): string => {
            if (error instanceof ProfileApiException) {
                if (error.missingScopes && error.missingScopes.length > 0) {
                    return t('Missing required scopes: {{scopes}}', { scopes: error.missingScopes.join(', ') });
                }
                return `${fallback}: ${error.message}`;
            }
            if (error instanceof Error) {
                return `${fallback}: ${error.message}`;
            }
            return fallback;
        },
        [t],
    );

    const loadProfileUsers = useCallback(async (): Promise<void> => {
        if (!therapistId || !accessToken) {
            setProfileUsersError(t('Unable to resolve therapist id from profile.'));
            setProfileUsers([]);
            return;
        }

        setIsLoadingProfileUsers(true);
        setProfileUsersError(null);

        try {
            const profileTargetUserIds = await getTargetUsersWithPermission(therapistId, 'rs-profile', {
                accessToken,
            });
            const resolvedUsers: PatientInfo[] = await Promise.all(
                profileTargetUserIds.map(async (userId): Promise<PatientInfo> => {
                    try {
                        const resolved = await resolveUserId(userId, { accessToken });
                        const usedName =
                            !resolved.firstName && !resolved.lastName
                                ? userId
                                : `${resolved.firstName ?? ''} ${resolved.lastName ?? ''}`;

                        return {
                            id: userId,
                            name: usedName,
                            firstName: resolved.firstName || undefined,
                            lastName: resolved.lastName || undefined,
                            username: resolved.username,
                            birthdate: resolved.birthdate || undefined,
                        };
                    } catch {
                        return {
                            id: userId,
                            name: userId,
                        };
                    }
                }),
            );

            setProfileUsers(resolvedUsers);
            setSelectedProfileUser((currentSelection) => {
                if (!currentSelection) {
                    return null;
                }
                return resolvedUsers.find((userInfo) => userInfo.id === currentSelection.id) || null;
            });
        } catch (error) {
            let errorMessage = t('Failed to load profile users.');
            if (error instanceof XAuthApiException) {
                if (error.missingScopes && error.missingScopes.length > 0) {
                    errorMessage = t('Missing required scopes: {{scopes}}', {
                        scopes: error.missingScopes.join(', '),
                    });
                } else {
                    errorMessage = `${t('Failed to load profile users')}: ${error.message}`;
                }
            } else if (error instanceof Error) {
                errorMessage = `${t('Failed to load profile users')}: ${error.message}`;
            }
            setProfileUsersError(errorMessage);
        } finally {
            setIsLoadingProfileUsers(false);
        }
    }, [therapistId, accessToken, t]);

    const fetchProfileForUser = useCallback(
        async (userId: string): Promise<void> => {
            if (!accessToken) {
                setSelectedUserProfile(null);
                setProfileError(t('You must be logged in to manage profile weeks.'));
                return;
            }

            setIsProfileLoading(true);
            setProfileError(null);

            try {
                let profileResource: TherapyProfile;
                try {
                    profileResource = await getProfile(userId, { accessToken });
                } catch (error) {
                    if (error instanceof ProfileApiException && error.status === 404) {
                        await initProfile(userId, { accessToken });
                        profileResource = await getProfile(userId, { accessToken });
                    } else {
                        throw error;
                    }
                }

                setSelectedUserProfile(profileResource);
            } catch (error) {
                setSelectedUserProfile(null);
                setProfileError(getProfileErrorMessage(error, t('Failed to load profile')));
            } finally {
                setIsProfileLoading(false);
            }
        },
        [accessToken, getProfileErrorMessage, t],
    );

    const handleNextWeek = useCallback(async (): Promise<void> => {
        if (!selectedProfileUser) {
            return;
        }
        if (!accessToken) {
            setProfileError(t('You must be logged in to manage profile weeks.'));
            return;
        }

        setIsUpdatingWeek(true);
        setProfileError(null);
        setProfileActionFeedback(null);

        try {
            await nextWeek(selectedProfileUser.id, { accessToken });
            await fetchProfileForUser(selectedProfileUser.id);
        } catch (error) {
            setProfileError(getProfileErrorMessage(error, t('Failed to update profile week')));
        } finally {
            setIsUpdatingWeek(false);
        }
    }, [selectedProfileUser, accessToken, fetchProfileForUser, getProfileErrorMessage, t]);

    const handlePreviousWeek = useCallback(async (): Promise<void> => {
        if (!selectedProfileUser) {
            return;
        }
        if (!accessToken) {
            setProfileError(t('You must be logged in to manage profile weeks.'));
            return;
        }

        setIsUpdatingWeek(true);
        setProfileError(null);
        setProfileActionFeedback(null);

        try {
            await previousWeek(selectedProfileUser.id, { accessToken });
            await fetchProfileForUser(selectedProfileUser.id);
        } catch (error) {
            setProfileError(getProfileErrorMessage(error, t('Failed to update profile week')));
        } finally {
            setIsUpdatingWeek(false);
        }
    }, [selectedProfileUser, accessToken, fetchProfileForUser, getProfileErrorMessage, t]);

    const handleStartNachsorge = useCallback(async (): Promise<void> => {
        if (!selectedProfileUser) {
            return;
        }
        if (!accessToken) {
            setNachsorgeStartError(t('You must be logged in to start aftercare.'));
            return;
        }

        setIsUpdatingWeek(true);
        setNachsorgeStartError(null);
        setProfileActionFeedback(null);

        try {
            await startNachsorgeWorkflow(selectedProfileUser.id, {
                questionnaire: { accessToken },
                profile: { accessToken },
            });
            await fetchProfileForUser(selectedProfileUser.id);
            setIsStartNachsorgeModalOpen(false);
            setProfileActionFeedback({ type: 'success', message: t('Aftercare started successfully.') });
        } catch (error) {
            if (error instanceof NachsorgeStartException && error.stage === 'profile') {
                setNachsorgeStartError(
                    t(
                        'The aftercare tasks exist, but the profile could not be moved to week 15. Please retry; existing tasks will be reused. Error: {{error}}',
                        { error: error.message },
                    ),
                );
            } else {
                const message = error instanceof Error ? error.message : t('Unknown error');
                setNachsorgeStartError(
                    t('The aftercare tasks could not be prepared. The profile was not changed. Error: {{error}}', {
                        error: message,
                    }),
                );
            }
        } finally {
            setIsUpdatingWeek(false);
        }
    }, [selectedProfileUser, accessToken, fetchProfileForUser, t]);

    const handleResetNachsorge = useCallback(async (): Promise<void> => {
        if (!selectedProfileUser) {
            return;
        }
        if (!accessToken) {
            setProfileActionFeedback({
                type: 'error',
                message: t('You must be logged in to reset aftercare.'),
            });
            return;
        }

        setIsUpdatingWeek(true);
        setProfileActionFeedback(null);

        try {
            await resetNachsorge(selectedProfileUser.id, { accessToken });
            await fetchProfileForUser(selectedProfileUser.id);
            setProfileActionFeedback({
                type: 'success',
                message: t('Aftercare reset to week 14. Tasks and answers were retained.'),
            });
        } catch (error) {
            setProfileActionFeedback({
                type: 'error',
                message: getProfileErrorMessage(error, t('Failed to reset aftercare')),
            });
        } finally {
            setIsUpdatingWeek(false);
        }
    }, [selectedProfileUser, accessToken, fetchProfileForUser, getProfileErrorMessage, t]);

    const handleRewardSave = useCallback(
        async (settings: RewardSettings): Promise<void> => {
            if (!selectedProfileUser || !accessToken) {
                throw new Error(t('You must be logged in to manage profile rewards.'));
            }

            setIsUpdatingRewards(true);
            try {
                await updateRewards(selectedProfileUser.id, settings, { accessToken });
                setSelectedUserProfile((currentProfile) =>
                    currentProfile
                        ? {
                              ...currentProfile,
                              ...settings,
                              manual_rewards: true,
                          }
                        : currentProfile,
                );
            } catch (error) {
                throw new Error(getProfileErrorMessage(error, t('Failed to save reward settings.')));
            } finally {
                setIsUpdatingRewards(false);
            }
        },
        [selectedProfileUser, accessToken, getProfileErrorMessage, t],
    );

    const handleRewardReset = useCallback(async (): Promise<void> => {
        if (!selectedProfileUser || !accessToken) {
            throw new Error(t('You must be logged in to manage profile rewards.'));
        }

        setIsUpdatingRewards(true);
        try {
            await resetRewards(selectedProfileUser.id, { accessToken });
            setSelectedUserProfile((currentProfile) =>
                currentProfile ? { ...currentProfile, manual_rewards: false } : currentProfile,
            );
        } catch (error) {
            throw new Error(getProfileErrorMessage(error, t('Failed to reset reward settings.')));
        } finally {
            setIsUpdatingRewards(false);
        }
    }, [selectedProfileUser, accessToken, getProfileErrorMessage, t]);

    useEffect(() => {
        if (mainTab === 'profile') {
            loadProfileUsers();
        }
    }, [mainTab, loadProfileUsers]);

    useEffect(() => {
        if (mainTab !== 'profile') {
            return;
        }
        if (!selectedProfileUser) {
            setSelectedUserProfile(null);
            setProfileError(null);
            setProfileActionFeedback(null);
            setIsStartNachsorgeModalOpen(false);
            setNachsorgeStartError(null);
            return;
        }
        setProfileActionFeedback(null);
        setIsStartNachsorgeModalOpen(false);
        setNachsorgeStartError(null);
        fetchProfileForUser(selectedProfileUser.id);
    }, [mainTab, selectedProfileUser, fetchProfileForUser]);

    const loadPatients = useCallback(async (): Promise<void> => {
        if (!therapistId || !accessToken) {
            setPatientsError(t('Unable to resolve therapist id from profile.'));
            return;
        }

        setIsLoadingPatients(true);
        try {
            const targetUserIds = await getTargetUsers(therapistId, { accessToken });

            const resolvedPatients: PatientInfo[] = await Promise.all(
                targetUserIds.map(async (userId): Promise<PatientInfo> => {
                    try {
                        const resolved: ResolvedUser = await resolveUserId(userId, { accessToken });

                        const usedName =
                            !resolved.firstName && !resolved.lastName
                                ? userId
                                : `${resolved.firstName ?? ''} ${resolved.lastName ?? ''}`;
                        return {
                            id: userId,
                            name: usedName,
                            firstName: resolved.firstName || undefined,
                            lastName: resolved.lastName || undefined,
                            username: resolved.username,
                            birthdate: resolved.birthdate || undefined,
                        };
                    } catch (resolveError) {
                        // If resolution fails for a specific user, still include them with their ID as name
                        return {
                            id: userId,
                            name: userId,
                        };
                    }
                }),
            );

            setPatients(resolvedPatients);
        } catch (error) {
            let errorMessage = t('Failed to load patients.');
            if (error instanceof XAuthApiException) {
                if (error.missingScopes && error.missingScopes.length > 0) {
                    errorMessage = t('Missing required scopes: {{scopes}}', {
                        scopes: error.missingScopes.join(', '),
                    });
                } else {
                    errorMessage = `${t('Failed to load patients')}: ${error.message}`;
                }
            } else if (error instanceof Error) {
                errorMessage = `${t('Failed to load patients')}: ${error.message}`;
            }
            setPatientsError(errorMessage);
            console.error('[FrontPage] Error loading patients:', error);
        } finally {
            setIsLoadingPatients(false);
        }
    }, [therapistId, accessToken, t]);

    const submitAssignment = async (
        selectedPatientIds: Set<string>,
        startsOn: Date,
        endsOn?: Date,
        isRecurring?: boolean,
        recurrenceType?: 'daily' | 'weekly',
        recurrenceEndDate?: Date,
    ): Promise<void> => {
        if (!selectedQuestionnaire || selectedPatientIds.size === 0) {
            setAssignError(t('Please select at least one patient.'));
            return;
        }
        if (!therapistId) {
            setAssignError(t('Unable to resolve therapist id from profile.'));
            return;
        }
        if (!accessToken) {
            setAssignError(
                isTherapyMaterial
                    ? t('You must be logged in to assign therapy material.')
                    : t('You must be logged in to assign questionnaires.'),
            );
            return;
        }

        if (endsOn && endsOn <= startsOn) {
            setAssignError(t('End date must be after start date.'));
            return;
        }
        if (isRecurring && recurrenceEndDate) {
            const lastStartDate = new Date(recurrenceEndDate);
            lastStartDate.setHours(23, 59, 59, 999);
            if (lastStartDate < startsOn) {
                setAssignError(t('Repeat until date must not be before the first start date.'));
                return;
            }
        }

        setAssignError(null);
        setIsAssigning(true);

        const patientIds = Array.from(selectedPatientIds);
        const errors: string[] = [];
        let successCount = 0;

        try {
            const assignmentWindows = createAssignmentWindows(
                startsOn,
                endsOn,
                isRecurring ? recurrenceType : undefined,
                isRecurring ? recurrenceEndDate : undefined,
            );

            // Assign questionnaire to each selected patient for each availability window.
            for (const patientId of patientIds) {
                for (const assignmentWindow of assignmentWindows) {
                    try {
                        const payload: ParticipationPayload = {
                            therapist: therapistId,
                            participant: patientId.trim(),
                            questionnaire: selectedQuestionnaire.id,
                            startsOn: assignmentWindow.startsOn.toISOString(),
                        };
                        if (assignmentWindow.endsOn) {
                            payload.endsOn = assignmentWindow.endsOn.toISOString();
                        }
                        await assignQuestionnaireToPatient(payload, { accessToken });
                        successCount++;
                    } catch (error) {
                        const message = error instanceof Error ? error.message : t('Unknown error');
                        const dateStr = assignmentWindow.startsOn.toLocaleDateString();
                        errors.push(`${patientId} (${dateStr}): ${message}`);
                    }
                }
            }

            if (errors.length === 0) {
                const totalTasks = patientIds.length * assignmentWindows.length;
                if (isRecurring) {
                    setAssignmentFeedback(
                        isTherapyMaterial
                            ? t('{{count}} recurring therapy material task(s) created successfully.', {
                                  count: totalTasks,
                              })
                            : t('{{count}} recurring task(s) created successfully.', { count: totalTasks }),
                    );
                } else {
                    setAssignmentFeedback(
                        isTherapyMaterial
                            ? t('Therapy material assigned to {{count}} patient(s).', { count: successCount })
                            : t('Questionnaire assigned to {{count}} patient(s).', { count: successCount }),
                    );
                }
                closeAssignModal();
            } else if (successCount > 0) {
                setAssignError(
                    t('Partially assigned: {{success}} succeeded, {{failed}} failed.', {
                        success: successCount,
                        failed: errors.length,
                    }) +
                        ' ' +
                        errors.join('; '),
                );
            } else {
                setAssignError(
                    (isTherapyMaterial
                        ? t('Failed to assign therapy material.')
                        : t('Failed to assign questionnaire.')) +
                        ' ' +
                        errors.join('; '),
                );
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown error');
            setAssignError(message);
        } finally {
            setIsAssigning(false);
        }
    };

    const downloadQuestionnaire = async () => {
        if (!selectedQuestionnaire) return;
        if (!accessToken) {
            setDownloadError(
                isTherapyMaterial
                    ? t('You must be logged in to download therapy material.')
                    : t('You must be logged in to download questionnaires.'),
            );
            return;
        }

        setDownloadError(null);
        setIsDownloading(true);

        try {
            const questionnaire = await getPractitionerQuestionnaireById(selectedQuestionnaire.id, { accessToken });
            const questionnaireJson = JSON.stringify(questionnaire, null, 2);
            const filename = `${
                selectedQuestionnaire.name ||
                selectedQuestionnaire.id ||
                (isTherapyMaterial ? 'therapy-material' : 'questionnaire')
            }.json`;
            const contentType = 'application/json;charset=utf-8;';

            /*eslint-disable */
            if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                const blob = new Blob([decodeURIComponent(encodeURI(questionnaireJson))], {
                    type: contentType,
                });
                (navigator as any).msSaveOrOpenBlob(blob, filename);
                /*eslint-enable */
            } else {
                const a = document.createElement('a');
                a.download = filename;
                a.href = 'data:' + contentType + ',' + encodeURIComponent(questionnaireJson);
                a.target = '_blank';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown error');
            setDownloadError(message);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDeleteTask = async (): Promise<void> => {
        if (!taskToDelete) return;
        if (!accessToken) {
            setDeleteTaskError(
                isTherapyMaterial
                    ? t('You must be logged in to delete therapy material assignments.')
                    : t('You must be logged in to delete tasks.'),
            );
            return;
        }

        setDeleteTaskError(null);
        setIsDeletingTask(true);

        try {
            await deleteTask(taskToDelete.id, { accessToken });
            setTaskToDelete(null);
            await fetchTasks();
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown error');
            setDeleteTaskError(message);
        } finally {
            setIsDeletingTask(false);
        }
    };

    const closeDeleteTaskModal = (): void => {
        setTaskToDelete(null);
        setDeleteTaskError(null);
    };

    const handleTaskClick = async (task: TaskSummary): Promise<void> => {
        const taskResource = taskResources.get(task.id);

        setSelectedTask(task);
        setQuestionnaireResponse(null);
        setResponseError(null);

        if (!accessToken) {
            setResponseError(t('You must be logged in to view responses.'));
            return;
        }

        // Regular questionnaire tasks only have a response after completion. Therapy material
        // deliberately remains requested and keeps its newest response in output[0].
        if (!shouldLoadTaskResponse(task.status, activeResourceKind, taskResource)) {
            return;
        }

        // For tasks that are not 'requested', try to fetch the response
        setIsLoadingResponse(true);

        try {
            // Use mock data if enabled
            if (USE_MOCK_DATA && task.id === 'mock-task-therapy-checkin-001') {
                console.log('[FrontPage] Using mock questionnaire response data');
                setQuestionnaireResponse(MOCK_QUESTIONNAIRE_RESPONSE as QuestionnaireResponse);
                setIsLoadingResponse(false);
                return;
            }

            // Use the stored task resource from the bundle
            if (!taskResource) {
                setResponseError(t('Task resource not available. Please refresh the task list.'));
                setIsLoadingResponse(false);
                return;
            }

            const responseId = getLatestTaskResponseId(taskResource);

            if (!responseId) {
                setResponseError(
                    isTherapyMaterial
                        ? t('No response found for this therapy material.')
                        : t('No response found for this task.'),
                );
                setIsLoadingResponse(false);
                return;
            }

            // Fetch the QuestionnaireResponse
            const response = await getQuestionnaireResponseById<QuestionnaireResponse>(responseId, {
                accessToken,
            });

            setQuestionnaireResponse(response);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown error');
            setResponseError(message);
            console.error('[FrontPage] Error loading QuestionnaireResponse:', error);
        } finally {
            setIsLoadingResponse(false);
        }
    };

    const closeTaskResponseModal = (): void => {
        setSelectedTask(null);
        setQuestionnaireResponse(null);
        setResponseError(null);
    };

    // Drop selected tasks that are no longer listed, e.g. after a task was deleted.
    useEffect(() => {
        setPdfSelectedTaskIds((current) => {
            const listed = new Set(tasks.map((task) => task.id));
            const next = new Set(Array.from(current).filter((taskId) => listed.has(taskId)));
            return next.size === current.size ? current : next;
        });
    }, [tasks]);

    const handleDownloadSelectedPdf = async (): Promise<void> => {
        const selectedTasks = tasks.filter((task) => pdfSelectedTaskIds.has(task.id));
        if (!accessToken || selectedTasks.length === 0) {
            return;
        }

        setPdfExportFeedback(null);
        setPdfExportProgress({ done: 0, total: selectedTasks.length });

        try {
            const { blob, filename, failedCount } = await createCombinedResponsesPdf({
                entries: selectedTasks.map((task) => ({
                    task,
                    responseId: getLatestTaskResponseId(taskResources.get(task.id)),
                })),
                kind: activeResourceKind,
                loadResponse: (responseId) =>
                    getQuestionnaireResponseById<QuestionnaireResponse>(responseId, { accessToken }),
                loadQuestionnaire: (questionnaireId) =>
                    getPractitionerQuestionnaireById<Questionnaire>(questionnaireId, { accessToken }),
                t,
                locale: i18n.language || 'en-US',
                exportedAt: new Date(),
                onProgress: (done, total) => setPdfExportProgress({ done, total }),
            });
            downloadBlob(blob, filename);
            if (failedCount > 0) {
                setPdfExportFeedback({
                    type: 'warning',
                    message: t(
                        '{{failed}} of {{total}} responses could not be exported. They are listed on the first page of the PDF.',
                        { failed: failedCount, total: selectedTasks.length },
                    ),
                });
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown error');
            setPdfExportFeedback({ type: 'error', message: `${t('Failed to create PDF')}: ${message}` });
        } finally {
            setPdfExportProgress(null);
        }
    };

    const taskPdfSelection: TaskListSelection = {
        selectedTaskIds: pdfSelectedTaskIds,
        isSelectable: (task) => shouldLoadTaskResponse(task.status, activeResourceKind, taskResources.get(task.id)),
        onChange: setPdfSelectedTaskIds,
        onDownloadPdf: handleDownloadSelectedPdf,
        progress: pdfExportProgress,
        feedback: pdfExportFeedback,
    };

    const handlePreviewQuestionnaire = async (questionnaire: QuestionnaireSummary): Promise<void> => {
        setQuestionnaireToPreview(questionnaire);
        setPreviewQuestionnaire(null);
        setPreviewError(null);

        if (!accessToken) {
            setPreviewError(
                isTherapyMaterial
                    ? t('You must be logged in to preview therapy material.')
                    : t('You must be logged in to preview questionnaires.'),
            );
            return;
        }

        setIsLoadingPreview(true);

        try {
            const fetchedQuestionnaire = await getPractitionerQuestionnaireById<Questionnaire>(questionnaire.id, {
                accessToken,
            });
            setPreviewQuestionnaire(fetchedQuestionnaire);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('Unknown error');
            setPreviewError(message);
            console.error('[FrontPage] Error loading Questionnaire for preview:', error);
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const closePreviewModal = (): void => {
        setQuestionnaireToPreview(null);
        setPreviewQuestionnaire(null);
        setPreviewError(null);
    };

    const startDeleteQuestionnaire = (): void => {
        if (selectedQuestionnaire) {
            setQuestionnaireToDelete(selectedQuestionnaire);
            setDeleteQuestionnaireError(null);
        }
    };

    const handleDeleteQuestionnaire = async (): Promise<void> => {
        if (!questionnaireToDelete) return;
        if (!accessToken) {
            setDeleteQuestionnaireError(
                isTherapyMaterial
                    ? t('You must be logged in to delete therapy material.')
                    : t('You must be logged in to delete questionnaires.'),
            );
            return;
        }

        setDeleteQuestionnaireError(null);
        setIsDeletingQuestionnaire(true);

        try {
            await deleteQuestionnaire(questionnaireToDelete.id, { accessToken });
            // Close both modals
            setQuestionnaireToDelete(null);
            closeAssignModal();
            // Refresh the questionnaire list
            await fetchQuestionnaires();
            // Show success feedback
            setAssignmentFeedback(
                isTherapyMaterial
                    ? t('Therapy material deleted successfully.')
                    : t('Questionnaire deleted successfully.'),
            );
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : isTherapyMaterial
                    ? t('Failed to delete therapy material')
                    : t('Failed to delete questionnaire');
            setDeleteQuestionnaireError(message);
        } finally {
            setIsDeletingQuestionnaire(false);
        }
    };

    const closeDeleteQuestionnaireModal = (): void => {
        setQuestionnaireToDelete(null);
        setDeleteQuestionnaireError(null);
    };

    const filteredProfileUsers = profileUsers.filter((userInfo) => {
        if (!profileSearchTerm.trim()) {
            return true;
        }
        const search = profileSearchTerm.toLowerCase();
        return (
            userInfo.name?.toLowerCase().includes(search) ||
            userInfo.username?.toLowerCase().includes(search) ||
            userInfo.id?.toLowerCase().includes(search)
        );
    });
    return (
        <>
            {suggestRestore && stateFromStorage && (
                <RestoreQuestionnaireModal
                    stateFromStorage={stateFromStorage}
                    onConfirm={onConfirmRestoreModal}
                    onDeny={onDenyRestoreModal}
                />
            )}
            {isLoading && (
                <Modal>
                    <div className="align-everything">
                        <SpinnerBox />
                    </div>
                    <p className="center-text">{t('Loading questionnaire...')}</p>
                </Modal>
            )}
            <FrontPageHeader onLogout={logout} activeMainTab={mainTab} onMainTabChange={handleMainTabChange} />
            <div className="frontpage">
                {mainTab !== 'profile' ? (
                    <>
                        {!isTherapyMaterial && !isNachsorge && (
                            <>
                                <input
                                    type="file"
                                    ref={uploadRef}
                                    onChange={uploadQuestionnaire}
                                    accept="application/JSON"
                                    style={{ display: 'none' }}
                                />
                                <Btn
                                    onClick={() => {
                                        history.push('/editor');
                                    }}
                                    title={t('New questionnaire')}
                                    variant="primary"
                                />
                                {` `}
                                <Btn
                                    onClick={() => {
                                        uploadRef.current?.click();
                                    }}
                                    title={t('Upload questionnaire')}
                                    variant="secondary"
                                />
                            </>
                        )}
                        {isTherapyMaterial && (
                            <>
                                <input
                                    type="file"
                                    ref={therapyMaterialUploadRef}
                                    onChange={selectTherapyMaterialFile}
                                    accept=".json,application/json"
                                    style={{ display: 'none' }}
                                />
                                <Btn
                                    onClick={() => {
                                        setTherapyMaterialReplaceTarget(null);
                                        therapyMaterialUploadRef.current?.click();
                                    }}
                                    title={t('Upload Therapy Material')}
                                    variant="secondary"
                                />
                                {therapyMaterialUploadError && !therapyMaterialToUpload && (
                                    <div className="frontpage__error" role="alert">
                                        {therapyMaterialUploadError}
                                    </div>
                                )}
                            </>
                        )}
                        <div className="frontpage__questionnaires">
                            <div className="frontpage__section-header">
                                <h3>
                                    {isNachsorge
                                        ? t('Aftercare responses')
                                        : managementViewMode === 'questionnaires'
                                        ? isTherapyMaterial
                                            ? t('Available Therapy Material')
                                            : t('Available questionnaires')
                                        : isTherapyMaterial
                                        ? t('Assigned Therapy Material')
                                        : t('Assigned Tasks')}
                                </h3>
                                {!isNachsorge && (
                                    <ViewToggle
                                        viewMode={viewMode}
                                        resourceKind={activeResourceKind}
                                        onViewModeChange={(mode) => {
                                            setViewMode(mode);
                                            if (mode === 'tasks') {
                                                setAssignmentFeedback(null);
                                            }
                                        }}
                                    />
                                )}
                            </div>
                            {managementViewMode === 'questionnaires' && (
                                <div className="frontpage__ownership-filter">
                                    <OwnershipFilter showOnlyMine={showOnlyMine} onChange={setShowOnlyMine} />
                                </div>
                            )}
                            {managementViewMode === 'questionnaires' ? (
                                <QuestionnaireList
                                    questionnaires={questionnaires}
                                    isLoading={isQuestionnaireListLoading}
                                    error={questionnaireListError}
                                    searchTerm={searchTerm}
                                    onSearchChange={setSearchTerm}
                                    onAssign={startAssign}
                                    onPreview={handlePreviewQuestionnaire}
                                    assignmentFeedback={assignmentFeedback}
                                    accessToken={accessToken}
                                    resourceKind={activeResourceKind}
                                />
                            ) : (
                                <TaskList
                                    tasks={tasks}
                                    questionnaires={questionnaires}
                                    isLoading={isTaskListLoading}
                                    error={taskListError}
                                    searchTerm={taskSearchTerm}
                                    onSearchChange={setTaskSearchTerm}
                                    onTaskClick={handleTaskClick}
                                    onDelete={isNachsorge ? undefined : (task) => setTaskToDelete(task)}
                                    accessToken={accessToken}
                                    resourceKind={activeResourceKind}
                                    selection={taskPdfSelection}
                                />
                            )}
                            {managementViewMode === 'questionnaires' && isQuestionnaireListLoadingMore && (
                                <div className="frontpage__loading">
                                    <SpinnerBox />
                                    <p>
                                        {isTherapyMaterial
                                            ? t('Loading therapy material...')
                                            : t('Loading questionnaires...')}
                                    </p>
                                </div>
                            )}
                            {managementViewMode === 'tasks' && isTaskListLoadingMore && (
                                <div className="frontpage__loading">
                                    <SpinnerBox />
                                    <p>
                                        {isNachsorge
                                            ? t('Loading aftercare tasks...')
                                            : isTherapyMaterial
                                            ? t('Loading assigned therapy material...')
                                            : t('Loading tasks...')}
                                    </p>
                                </div>
                            )}
                            {((managementViewMode === 'questionnaires' && hasMoreQuestionnaires) ||
                                (managementViewMode === 'tasks' && hasMoreTasks)) && (
                                <div ref={paginationSentinelRef} style={{ height: 1 }} aria-hidden="true" />
                            )}
                        </div>
                    </>
                ) : (
                    <div className="frontpage__profile">
                        <div className="frontpage__infotext">
                            {t('Select an authorized user to manage their therapy week and rewards.')}
                        </div>
                        <div className="frontpage__profile-layout">
                            <div className="frontpage__profile-users">
                                <h3>{t('Authorized users')}</h3>
                                {isLoadingProfileUsers ? (
                                    <div className="frontpage__loading">
                                        <SpinnerBox />
                                        <p>{t('Loading authorized users...')}</p>
                                    </div>
                                ) : profileUsersError ? (
                                    <div className="frontpage__error">{profileUsersError}</div>
                                ) : (
                                    <>
                                        {profileUsers.length > 0 && (
                                            <input
                                                type="text"
                                                className="frontpage__search-input"
                                                placeholder={t('Search authorized users...')}
                                                value={profileSearchTerm}
                                                onChange={(event) => setProfileSearchTerm(event.target.value)}
                                            />
                                        )}
                                        {filteredProfileUsers.length > 0 ? (
                                            <ul className="frontpage__patient-list">
                                                {filteredProfileUsers.map((userInfo) => (
                                                    <li
                                                        key={userInfo.id}
                                                        className={`frontpage__patient-item ${
                                                            selectedProfileUser?.id === userInfo.id
                                                                ? 'frontpage__patient-item--selected'
                                                                : ''
                                                        }`}
                                                        onClick={() => {
                                                            if (!isUpdatingWeek && !isUpdatingRewards) {
                                                                setSelectedProfileUser(userInfo);
                                                            }
                                                        }}
                                                        role="button"
                                                        aria-disabled={isUpdatingWeek || isUpdatingRewards}
                                                        tabIndex={isUpdatingWeek || isUpdatingRewards ? -1 : 0}
                                                        onKeyDown={(event: React.KeyboardEvent) => {
                                                            if (
                                                                !isUpdatingWeek &&
                                                                !isUpdatingRewards &&
                                                                (event.key === 'Enter' || event.key === ' ')
                                                            ) {
                                                                event.preventDefault();
                                                                setSelectedProfileUser(userInfo);
                                                            }
                                                        }}
                                                    >
                                                        <div className="frontpage__patient-info">
                                                            <span className="frontpage__patient-name">
                                                                {userInfo.name}
                                                            </span>
                                                            <span className="frontpage__list-meta">
                                                                User ID: {userInfo.id}
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : profileUsers.length > 0 ? (
                                            <div className="frontpage__empty">{t('No authorized users found')}</div>
                                        ) : (
                                            <div className="frontpage__empty">{t('No authorized users available')}</div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="frontpage__profile-details">
                                <h3>{t('Profile')}</h3>
                                {!selectedProfileUser ? (
                                    <p className="frontpage__empty">
                                        {t('Select a user to manage therapy week and rewards.')}
                                    </p>
                                ) : isProfileLoading ? (
                                    <div className="frontpage__loading">
                                        <SpinnerBox />
                                        <p>{t('Loading profile...')}</p>
                                    </div>
                                ) : profileError ? (
                                    <div className="frontpage__error">{profileError}</div>
                                ) : (
                                    selectedUserProfile && (
                                        <>
                                            <div className="frontpage__profile-details-grid">
                                                <div className="frontpage__profile-details-row">
                                                    <span className="frontpage__profile-label">{t('User')}</span>
                                                    <span>{selectedProfileUser.name}</span>
                                                </div>
                                                <ProfileAgeRow birthdate={selectedProfileUser.birthdate} />
                                                <div className="frontpage__profile-details-row">
                                                    <span className="frontpage__profile-label">
                                                        {t('Therapy week')}
                                                    </span>
                                                    <span>{selectedUserProfile.therapy.week}</span>
                                                </div>
                                                <div className="frontpage__profile-details-row">
                                                    <span className="frontpage__profile-label">
                                                        {t('Therapy date')}
                                                    </span>
                                                    <span>{selectedUserProfile.therapy.date}</span>
                                                </div>
                                            </div>
                                            {profileActionFeedback && (
                                                <div
                                                    className={
                                                        profileActionFeedback.type === 'success'
                                                            ? 'frontpage__success'
                                                            : 'frontpage__error'
                                                    }
                                                    role={profileActionFeedback.type === 'error' ? 'alert' : 'status'}
                                                >
                                                    {profileActionFeedback.message}
                                                </div>
                                            )}
                                            <ProfileWeekActions
                                                week={selectedUserProfile.therapy.week}
                                                isBusy={isUpdatingWeek || isUpdatingRewards || isProfileLoading}
                                                onPreviousWeek={handlePreviousWeek}
                                                onNextWeek={handleNextWeek}
                                                onStartNachsorge={() => {
                                                    setNachsorgeStartError(null);
                                                    setIsStartNachsorgeModalOpen(true);
                                                }}
                                                onResetNachsorge={handleResetNachsorge}
                                            />
                                            <ProfileEnergySettings
                                                key={selectedProfileUser.id}
                                                profile={selectedUserProfile}
                                                birthdate={selectedProfileUser.birthdate}
                                                isBusy={isUpdatingWeek || isUpdatingRewards}
                                                onSave={handleRewardSave}
                                                onReset={handleRewardReset}
                                            />
                                        </>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {isStartNachsorgeModalOpen && (
                <StartNachsorgeModal
                    patient={selectedProfileUser}
                    currentWeek={selectedUserProfile?.therapy.week ?? null}
                    isStarting={isUpdatingWeek}
                    error={nachsorgeStartError}
                    onClose={() => {
                        if (!isUpdatingWeek) {
                            setIsStartNachsorgeModalOpen(false);
                            setNachsorgeStartError(null);
                        }
                    }}
                    onConfirm={handleStartNachsorge}
                />
            )}
            <AssignQuestionnaireModal
                questionnaire={selectedQuestionnaire}
                patients={patients}
                isLoadingPatients={isLoadingPatients}
                patientsError={patientsError}
                isAssigning={isAssigning}
                assignError={assignError}
                downloadError={downloadError}
                isDownloading={isDownloading}
                onClose={closeAssignModal}
                onAssign={submitAssignment}
                onDownload={downloadQuestionnaire}
                onLoadPatients={loadPatients}
                onDelete={startDeleteQuestionnaire}
                onEdit={startEditQuestionnaire}
                isPreparingEdit={isPreparingEdit}
                editError={editError}
                resourceKind={activeResourceKind}
            />
            <DeleteQuestionnaireModal
                questionnaire={questionnaireToDelete}
                isDeleting={isDeletingQuestionnaire}
                error={deleteQuestionnaireError}
                onClose={closeDeleteQuestionnaireModal}
                onDelete={handleDeleteQuestionnaire}
                resourceKind={activeResourceKind}
            />
            <DeleteTaskModal
                task={taskToDelete}
                isDeleting={isDeletingTask}
                error={deleteTaskError}
                onClose={closeDeleteTaskModal}
                onDelete={handleDeleteTask}
                resourceKind={activeResourceKind}
            />
            {selectedTask && (
                <TaskResponseModal
                    task={selectedTask}
                    questionnaireResponse={questionnaireResponse}
                    isLoading={isLoadingResponse}
                    error={responseError}
                    onClose={closeTaskResponseModal}
                    accessToken={accessToken}
                    resourceKind={activeResourceKind}
                />
            )}
            {questionnaireToPreview &&
                (isTherapyMaterial ? (
                    <TherapyMaterialPreviewModal
                        questionnaire={previewQuestionnaire}
                        isLoading={isLoadingPreview}
                        error={previewError}
                        onClose={closePreviewModal}
                    />
                ) : (
                    <QuestionnairePreviewModal
                        questionnaire={previewQuestionnaire}
                        isLoading={isLoadingPreview}
                        error={previewError}
                        onClose={closePreviewModal}
                    />
                ))}
            {therapyMaterialToUpload && isTherapyMaterial && (
                <TherapyMaterialPreviewModal
                    questionnaire={therapyMaterialToUpload}
                    isLoading={false}
                    error={null}
                    onClose={isUploadingTherapyMaterial ? (): void => undefined : closeTherapyMaterialUploadPreview}
                    onUpload={confirmTherapyMaterialUpload}
                    isUploading={isUploadingTherapyMaterial}
                    uploadError={therapyMaterialUploadError}
                    isPrivate={isTherapyMaterialPrivate}
                    onPrivateChange={setIsTherapyMaterialPrivate}
                    replaceTargetName={therapyMaterialReplaceTarget?.displayName}
                />
            )}
        </>
    );
};

export default FrontPage;
